// Transportation Management System Types

export interface Address {
  street_address: string;
  city: string;
  province_state?: string;
  postal_zip_code?: string;
  country?: string;
}

export type OrderStatus = 
  | 'booked'
  | 'confirmed'
  | 'canceled'
  | 'in_transit'
  | 'delivered'
  | 'tender_received';



export type TractorCapability = 'VO' | 'NVO' | 'Manual';

export type TractorBase = 'Peterbilt' | 'Volvo' | 'International' | 'Daimler';

export type DriverCertification = 'VO' | 'NVO' | 'Manual';

export type TripType = 'NVO' | 'SVO' | 'Manual' | 'CI';

export type DriverShift = 'AM' | 'PM';

export interface Flight {
  id: string;
  name: string;
  days: number[];
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: string;
  organization_name: string;
  short_code: string;
  email?: string | null;
  street_address?: string;
  city?: string;
  province_state?: string;
  postal_zip_code?: string;
  country?: string;
  requires_tender_id: boolean;
  requires_bol: boolean;
  requires_tender: boolean;
  authorized_nvo: boolean;
  created_at: string;
  updated_at: string;
}

export interface Location {
  id: string;
  customer_id?: string | null;
  short_code: string;
  customer?: Customer | null;
  street_address?: string;
  city?: string;
  province_state?: string;
  postal_zip_code?: string;
  country?: string;
  track_in_yms: boolean;
  latitude?: number | null;
  longitude?: number | null;
  origin_latitude: number;
  origin_longitude: number;
  destination_latitude: number;
  destination_longitude: number;
  created_at: string;
  updated_at: string;
}

export interface Tractor {
  id: string;
  name: string;
  capabilities: TractorCapability[];
  base_truck?: TractorBase;
  home_base_id?: string;
  home_base?: Location;
  samsara_id?: string;
  vin?: string;
  order_ids?: string[];
  work_order_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface TractorMaintenance {
  id: string;
  tractor_id: string;
  start_time: string;
  duration_hours?: number;
  description?: string;
  created_at: string;
}

export interface Driver {
  id: string;
  name: string;
  email?: string;
  slack_handle?: string;
  manager?: string;
  home_base_id?: string;
  home_base?: Location;
  flight_id?: string;
  flight?: Flight;
  certification?: DriverCertification;
  shift?: DriverShift;
  order_ids?: string[];
  created_at: string;
  updated_at: string;
}

export interface RecurringOrderTemplate {
  id: string;
  unique_id: string;
  billing_party_id?: string;
  billing_party?: Customer;
  ship_from_location_id?: string;
  ship_from_location?: Location;
  ship_to_location_id?: string;
  ship_to_location?: Location;
  departure_time: string;
  trip_duration_minutes: number;
  recurrence_days: number[];
  start_date: string;
  end_date: string;
  trailer_type_id?: string | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/** Per-leg status signals used for the displayed order rollup status. */
export interface OrderLegStatusSummary {
  driver_status?: string | null;
  eapi_status?: string | null;
  leg_sequence?: number | null;
  /** legs.trailer_id: the leg's own trailer number. */
  trailer_id?: string | null;
}



export interface Order {
  id: string;
  unique_id: string;
  status: OrderStatus;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_location_id?: string | null;
  ship_from_province_state?: string;
  ship_from_postal_zip_code?: string;
  ship_from_country?: string;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_location_id?: string | null;
  ship_to_province_state?: string;
  ship_to_postal_zip_code?: string;
  ship_to_country?: string;
  ready_time: string;
  requested_delivery_time: string;
  ready_time_end: string;
  requested_delivery_time_end: string;
  billing_party_id?: string;
  billing_party?: Customer;
  trailer_id?: string;
  trailer_type_id?: string | null;
  tender_id?: string;
  assigned_tractor_id?: string;
  assigned_tractor?: Tractor;
  assigned_operator_id?: string;
  assigned_operator?: Driver;
  assigned_operator_2_id?: string | null;
  assigned_operator_2?: Driver | null;
  is_recurring: boolean;
  recurrence_days?: number[];
  recurrence_end_date?: string;
  recurrence_group_id?: string;
  recurring_order_template_id?: string;
  recurring_order_template?: RecurringOrderTemplate;
  is_work_order: boolean;
  work_order_description?: string;
  parent_order_id?: string | null;
  leg_sequence?: number | null;
  trip_type?: TripType | null;
  eapi_trip_id?: string | null;
  created_by?: string;
  external_order_id?: string | null;
  /**
   * Number of leg rows attached to this parent (Phase 4+). Attached by
   * `useOrdersQuery` from a parallel `public.legs` aggregate. Undefined when
   * the row came from a code path that does not enrich it. UI multi-leg
   * detection should treat `leg_count > 1` as multi-leg with the legacy
   * `leg_sequence === 0` heuristic as a fallback.
   */
  leg_count?: number;
  /**
   * Minimal per-leg status signals used to derive the displayed rollup status
   * (see `src/lib/order-rollup-status.ts`). Attached by queries that hydrate
   * legs; undefined elsewhere, in which case the stored status is shown as-is.
   */
  legs?: OrderLegStatusSummary[];
  created_at: string;
  updated_at: string;
}

export interface WorkOrder {
  id: string;
  unique_id: string;
  tractor_id: string;
  start_time: string;
  duration_hours: number;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface DriverDayOff {
  id: string;
  driver_id: string;
  date: string;
  reason?: string;
  created_at: string;
}

export interface OrderChangeLog {
  id: string;
  order_id: string;
  changed_by: string;
  field_name: string;
  old_value?: string;
  new_value?: string;
  changed_at: string;
}

export interface OrderBolImage {
  id: string;
  order_id: string;
  image_url: string;
  created_at: string;
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  booked: 'Booked',
  confirmed: 'Confirmed',
  canceled: 'Canceled',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  tender_received: 'Tender Received',
};

export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];
